import React, { useEffect, useState } from "react";
import axios from "axios";
import { UiComponents, UnoStoreHooks, EditIcon, UploadIcon } from "@simulanisindia/ui";
import Default from "../../../assets/img/default.jpg";
import { debounce, VALIDATE_REGEX, isValidFile, modalName } from "../../../utils/general";
import { ACCEPTED_IMAGE_TYPES } from "../../../constant/constants";
import { UPDATE_USER, UPLOAD_USER_AVATAR } from "../../../apiClient/apiEndpoints";
import apiClient from "../../../apiClient";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { removeUserData } from "../../../store/slice/userSlice";
import { PasswordKeyIcon } from "../../../SvgIconsData";
import { useModalContext } from "../../../modal/context/ModalProvider";
import PhoneNumberInput from "../../../components/PhoneNumberInput";

const { useUnoSelector } = UnoStoreHooks;
const { SimContainer, SimRow, SimColumns, SimInputBar, SimToggleSwitch, DropdownSearch } = UiComponents;

const ProfileInformation = ({ userDetails, handleGetUserDetails, UUID, isMobileView }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const configData = useUnoSelector((state) => state.config);
  const configSlice = useSelector((state) => state.configSlice);
  const userData = useUnoSelector((state) => state.user);
  const [isEditing, setIsEditing] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUploadError, setImageUploadError] = useState(null);
  const [roleTypes, setRoleTypes] = useState([]);
  const [loading, setLoading] = useState({
    profile: false,
    information: false,
  });

  const { openModal } = useModalContext();
  const handleOpenChangePasswordModal = () => {
    openModal({
      modalName: modalName.PASSWORD_CHANGE,
      type: "update",
      UUID: userDetails.uuid,
      params,
      user: {
        email: userDetails.email,
      },
    });
  };

  const [formData, setFormData] = useState({
    avatar: null,
    name: "",
    designation: "",
    email: "",
    uid: "",
    phone: "",
    role: null,
    active: true,
  });

  const [profileFields, setProfileFields] = useState([
    { key: "name", label: "Name*", type: "text", editable: true, validate: true },
    { key: "designation", label: "Designation", type: "text", editable: true, validate: true },
    { key: "email", label: "Email address", type: "email", editable: false, validate: true },
    { key: "uid", label: "UID", type: "text", editable: false, validate: true },
    { key: "phone", label: "Phone", type: "tel", editable: true, validate: false },
    { key: "role", label: "Role", type: "dropdown", editable: true, validate: false },
  ]);

  useEffect(() => {
    if (!isMobileView) {
      setProfileFields((pre) => {
        const statusExists = pre.some((field) => field.key === "status");
        if (!statusExists) {
          return [...pre, { key: "status", label: "Status", type: "status", editable: true }];
        }
        return pre;
      });
    }
  }, [isMobileView]);

  const handleReset = () => {
    setLoading({
      profile: false,
      information: false,
    });
    setIsEditing(false);
    setImagePreview(null);
    setImageUploadError(null);
  };

  useEffect(() => {
    handleReset();
    setFormData({
      avatar: userDetails?.avatar || null,
      name: userDetails?.name,
      designation: userDetails?.designation,
      email: userDetails.email,
      uid: userDetails.uid,
      phone: userDetails?.phone,
      active: userDetails?.active,
      role: userDetails?.role || null,
    });
  }, [UUID, userDetails]);

  const handleInputChange = (key, value, validate) => {
    if (validate && VALIDATE_REGEX.test(value)) return;
    setFormData({ ...formData, [key]: value });
  };

  const handleImageChange = async (e) => {
    try {
      setImageUploadError(null);
      const file = e.target.files[0];
      if (!file) return;
      if (!isValidFile(file, ACCEPTED_IMAGE_TYPES)) {
        setImageUploadError("Invalid Image");
        return;
      }
      const formData = new FormData();
      formData.append("file", file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      let { data } = await axios.post(`${configData.config.UNO_URL}${UPLOAD_USER_AVATAR}/${UUID}`, formData, {
        headers: {
          Authorization: `Bearer ${userData?.token}`,
          "Content-Type ": "multipart/form-data",
        },
      });
      setImagePreview(data.url);
    } catch (error) {
      console.log(error);
      setImageUploadError(error.message);
    }
  };

  const handleSaveProfileImage = async () => {
    if (loading.profile) return;
    try {
      setLoading((pre) => ({ ...pre, profile: true }));
      let bodyData = {
        avatar: imagePreview,
      };
      await apiClient.post(`${configData.config.UNO_URL}${UPDATE_USER}/${UUID}`, bodyData);
      if (handleGetUserDetails) handleGetUserDetails();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading((pre) => ({ ...pre, profile: false }));
    }
  };

  const handleSaveUserDetail = async () => {
    if (loading.information) return;
    try {
      if (formData.name.trim().length === 0) return;
      setLoading((pre) => ({ ...pre, information: true }));
      let bodyData = {
        name: formData.name,
        designation: formData.designation,
        phone: formData.phone,
        active: formData.active,
        role: formData?.role?.value || null,
      };
      await apiClient.post(`${configData.config.UNO_URL}${UPDATE_USER}/${UUID}`, bodyData);
      setIsEditing(false);
      if (handleGetUserDetails) handleGetUserDetails();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading((pre) => ({ ...pre, information: false }));
    }
  };

  useEffect(() => {
    let typeList = configSlice?.roles.map((role) => {
      return { value: role.id, label: role.name };
    });
    setRoleTypes(typeList);
  }, [configSlice]);

  const handleLogout = async () => {
    try {
      await apiClient.post(`${configData.config.UNO_URL}/api/auth/logout`, {});
    } catch (error) {
      console.log(error);
    } finally {
      localStorage.removeItem("token");
      dispatch(removeUserData());
      navigate(`/gateway`);
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      avatar: userDetails?.avatar || null,
      name: userDetails?.name,
      designation: userDetails?.designation,
      email: userDetails.email,
      uid: userDetails.uid,
      phone: userDetails?.phone,
      active: userDetails?.active,
      role: userDetails?.role || null,
    });
    setIsEditing(false);
  };

  const handleDebounceUploadProfile = debounce(handleSaveProfileImage, 500);
  const handleDebounceDetail = debounce(handleSaveUserDetail, 500);

  return (
    <>
      {/* Profile Card */}
      <div className={!isMobileView ? "carddata colorborder" : "mobileCard"}>
        <div className="profileinfo justify-content-start">
          <div className="profileimg">
            <div>
              <input
                type="file"
                id="fileInput"
                onChange={handleImageChange}
                style={{ display: "none" }}
                accept={ACCEPTED_IMAGE_TYPES.join(", ")}
              />
              <label htmlFor="fileInput" style={{ cursor: "pointer" }}>
                <div className="profile-image-upload">
                  {imagePreview ? (
                    <div className="uploadoverlay">
                      <img src={imagePreview} alt="" referrerPolicy={"no-referrer"} />
                      <span className="uploadicon">
                        <UploadIcon />
                      </span>
                    </div>
                  ) : (
                    <div className="uploadoverlay">
                      <img src={userDetails?.avatar || Default} alt="" referrerPolicy={"no-referrer"} />
                      <span className="uploadicon">
                        <UploadIcon />
                      </span>
                    </div>
                  )}
                </div>
              </label>
            </div>
            {imageUploadError && <p className="inputError">{imageUploadError}</p>}
          </div>
          {!isMobileView && (
            <div className="profileinfocont">
              <h6 className="card-heading"> {userDetails?.name || ""} </h6>
              <p className="m-0"> {userDetails?.designation || ""} </p>
              <p className="m-0"> {userDetails?.email || ""} </p>
              <p className="m-0"> {userDetails?.uid || ""} </p>
            </div>
          )}
          {isMobileView && (
            <div className="d-flex pass-changes">
              <div className="edit-btn mb-2" onClick={() => handleOpenChangePasswordModal()}>
                {" "}
                Change Password
                <PasswordKeyIcon />
              </div>
              {isEditing ? (
                <div className="display-flex gap-2">
                  <button className="roundednormalbtn d-flex align-items-center gap-1" onClick={handleCancelEdit}>
                    Cancel
                  </button>
                  <button className="roundednormalbtn d-flex align-items-center gap-1" onClick={handleDebounceDetail}>
                    {loading.information ? "Saving" : "Save"}
                  </button>
                </div>
              ) : (
                <button className="roundednormalbtn d-flex align-items-center gap-1" onClick={() => setIsEditing(true)}>
                  Edit <EditIcon />
                </button>
              )}
            </div>
          )}
          {imagePreview && (
            <button onClick={handleDebounceUploadProfile} className="roundednormalbtn d-flex align-items-center gap-1">
              {loading.profile ? "saving" : "Save"}
            </button>
          )}
        </div>
      </div>

      {/* Personal Information */}
      {/* <div className={!isMobileView ? "carddata colorborder " : "mobileCard pb-6"}> */}
      <div className={!isMobileView ? "carddata perinfo" : "mobileCard pb-6"}>
        <div className="profileinfo">
          {!isMobileView && <h6 className="card-heading">Personal Information</h6>}
          <div>
            {!isMobileView && (
              <>
                {isEditing ? (
                  <div className="display-flex gap-3">
                    <button className="roundednormalbtn d-flex align-items-center gap-1" onClick={handleCancelEdit}>
                      Cancel
                    </button>
                    <button className="roundednormalbtn d-flex align-items-center gap-1" onClick={handleDebounceDetail}>
                      {loading.information ? "Saving" : "Save"}
                    </button>
                  </div>
                ) : (
                  <button
                    className="roundednormalbtn d-flex align-items-center gap-1"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit <EditIcon />
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="userdata">
          <SimContainer fluid>
            <SimRow>
              {profileFields.map((field) => {
                let isDisabled = UUID === userData.user.uuid || !field.editable || !isEditing;

                if (field.type === "dropdown") {
                  return (
                    <SimColumns sm="5">
                      <DropdownSearch
                        label="Role Type"
                        error={null}
                        selectProps={{
                          options: roleTypes,
                          value: formData?.role || null,
                          isDisabled: isDisabled,
                          onChange: (e) => handleInputChange("role", e, false),
                        }}
                      />
                    </SimColumns>
                  );
                }

                if (field.type === "status") {
                  return (
                    <div className="toggleSwitch statusMob">
                      <SimColumns sm="5">
                        <SimToggleSwitch
                          label="Status*"
                          checked={formData.active}
                          onChange={() => setFormData((pre) => ({ ...pre, ["active"]: !pre.active }))}
                          toggleKeyNames="Active"
                          ToggleKeyClass="active-inactive"
                          disabled={isDisabled}
                        />
                      </SimColumns>
                    </div>
                  );
                }

                if (field.type === "tel") {
                  return (
                    <div className="col-sm-5 mb-3">
                      <div className="sim-input">
                        <label htmlFor="phone">Phone Number*</label>
                        {isEditing ? (
                          <PhoneNumberInput
                            value={formData?.phone || ""}
                            onChange={(val) => setFormData((pre) => ({ ...pre, phone: val }))}
                          />
                        ) : (
                          <>
                            <p className="mt-1">{formData[field.key] || "NA"}</p>
                          </>
                        )}
                      </div>
                    </div>
                  );
                }

                return (
                  <>
                    <SimColumns sm="5" key={field.key}>
                      <label>{field.label}</label>
                      <div className="sim-input-parent">
                        {isEditing && field.editable ? (
                          <SimInputBar
                            type={field.type}
                            value={formData[field.key]}
                            onChange={(e) => handleInputChange(field.key, e.target.value, field.validate)}
                          />
                        ) : (
                          <p className="mt-1">{formData[field.key] || "NA"}</p>
                        )}
                      </div>
                    </SimColumns>
                  </>
                );
              })}
            </SimRow>
          </SimContainer>
        </div>

        {isMobileView && (
          <div className="logoutBtn" onClick={handleLogout}>
            <p>Sign Out</p>
          </div>
        )}
      </div>
    </>
  );
};

export default ProfileInformation;
